import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { useMe } from '../../../shared/hooks/useMe'
import { deprecated_useSetToast } from '../../../shared/hooks/useSetToast'
import { Application, PostApplicationFields } from '../../../shared/models/applicationModel'
import { Company } from '../../../shared/models/companyModel'
import { RequestResponse } from '../../../shared/services/requestServices'
import {
    deleteApplicationService,
    getApplicationsService,
    createApplicationService
} from '@/modules/applications/services/applicationsServices'

const applicationsQueryKeys = {
    root: ['applications'],
    applications: (companyId: Company['id']): string[] => [...applicationsQueryKeys.root, companyId]
}

type UseApplications = {
    enabled?: boolean
}

export const useApplications = (options?: UseApplications): UseQueryResult<Application[] | undefined> => {
    const { enabled } = options ?? {}
    const { companyId } = useMe()

    return useQuery({
        queryKey: applicationsQueryKeys.applications(companyId),
        queryFn: () => getApplicationsService(companyId),
        select: (response) => response?.body,
        enabled: enabled ?? true
    })
}

export const useCreateApplication = (
    shouldTriggerToaster = false
): UseMutationResult<RequestResponse<Application> | undefined, unknown, PostApplicationFields, unknown> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const setToast = deprecated_useSetToast()

    return useMutation({
        mutationFn: (application) => createApplicationService(companyId, application),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: applicationsQueryKeys.applications(companyId) })
            shouldTriggerToaster && setToast('postApplication', 'positive')
        },
        onError: () => {
            shouldTriggerToaster && setToast('postApplication', 'negative')
        }
    })
}

export const useDeleteApplication = (): UseMutationResult<
    RequestResponse<Application> | undefined,
    unknown,
    string,
    unknown
> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (id: string) => deleteApplicationService(companyId, id)
    })
}

export const useInvalidateApplications = () => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const invalidateApplications = async () => {
        await queryClient.invalidateQueries({
            queryKey: applicationsQueryKeys.applications(companyId)
        })
    }

    return {
        invalidateApplications
    }
}
